<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { IconRefresh } from '@tabler/icons-vue'
import AppTooltip from './AppTooltip.vue'

const { t } = useI18n()

interface Props {
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'info' | 'gray' | 'purple';
  size?: 'sm' | 'md' | 'lg';
  iconOnly?: boolean;
  iconOnlyMobile?: boolean;
  title?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  disabled: false,
  loading: false,
  variant: 'primary',
  size: 'md',
  iconOnly: false,
  iconOnlyMobile: false,
  title: undefined,
});

// Define component events
defineEmits(['click']);

// Size classes
const sizeClasses = computed(() => {
  // Pro icon-only tlačítka používáme čtvercový padding
  if (props.iconOnly) {
    const iconOnlySizes = {
      sm: 'p-2',
      md: 'p-3',
      lg: 'p-4'
    };
    return iconOnlySizes[props.size];
  }

  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3'
  };

  // Pro iconOnly mobile používáme vždy p-2.5 na mobilu
  if (props.iconOnlyMobile) {
    return `p-2.5 sm:${sizes[props.size]}`;
  }

  return sizes[props.size];
});

// Icon size classes
const iconSizeClasses = computed(() => {
  const sizes = {
    sm: 'w-3.5 h-3.5',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  // Pro iconOnly mobile je ikona větší na mobilu
  if (props.iconOnlyMobile) {
    return `w-5 h-5 sm:${sizes[props.size]}`;
  }

  return sizes[props.size];
});

const baseClasses = 'rounded-lg font-medium font-body transition-all duration-100 focus:outline-none disabled:opacity-50 inline-flex items-center justify-center';

const variantClasses = computed(() => ({
  primary:
    'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 hover:border-amber-500/40 focus:ring-amber-400',
  secondary:
    'bg-base-200 text-gray-300 hover:bg-base-100 hover:text-gray-200 focus:ring-amber-400',
  danger:
    'bg-red-500/10 text-red-400 hover:bg-red-500/20 focus:ring-red-500',
  success:
    'bg-green-500/10 text-green-400 hover:bg-green-500/20 focus:ring-green-500',
  info:
    'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 focus:ring-blue-500',
  gray:
    'bg-base-300 border border-white/[0.06] text-gray-300 hover:bg-base-200 hover:text-gray-200 hover:border-white/10 focus:ring-amber-400 shadow-sm',
  purple:
    'bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 focus:ring-purple-500',
}));
</script>

<template>
  <AppTooltip v-if="title" :text="title">
    <button
      :type="type"
      :disabled="disabled || loading"
      :class="[baseClasses, variantClasses[variant], sizeClasses]"
      @click="$emit('click', $event)"
    >
      <span v-if="loading" class="flex items-center gap-2">
        <IconRefresh :class="iconSizeClasses" class="animate-spin" />
        <span v-if="!iconOnly && !iconOnlyMobile">{{ t('common.status.loading') }}</span>
      </span>
      <template v-else>
        <!-- Icon slot -->
        <span v-if="$slots.icon" class="inline-flex items-center justify-center [&>svg]:w-full [&>svg]:h-full" :class="[iconSizeClasses, { 'sm:mr-2': !iconOnly && $slots.default }]">
          <slot name="icon" />
        </span>

        <!-- Text slot - hidden on mobile if iconOnlyMobile -->
        <span v-if="$slots.default && !iconOnly" :class="{ 'hidden sm:inline': iconOnlyMobile }">
          <slot />
        </span>
      </template>
    </button>
  </AppTooltip>

  <button
    v-else
    :type="type"
    :disabled="disabled || loading"
    :class="[baseClasses, variantClasses[variant], sizeClasses]"
    @click="$emit('click', $event)"
  >
    <span v-if="loading" class="flex items-center gap-2">
      <IconRefresh :class="iconSizeClasses" class="animate-spin" />
      <span v-if="!iconOnly && !iconOnlyMobile">{{ t('common.status.loading') }}</span>
    </span>
    <template v-else>
      <!-- Icon slot -->
      <span v-if="$slots.icon" class="inline-flex items-center justify-center [&>svg]:w-full [&>svg]:h-full" :class="[iconSizeClasses, { 'sm:mr-2': !iconOnly && $slots.default }]">
        <slot name="icon" />
      </span>

      <!-- Text slot - hidden on mobile if iconOnlyMobile -->
      <span v-if="$slots.default && !iconOnly" :class="{ 'hidden sm:inline': iconOnlyMobile }">
        <slot />
      </span>
    </template>
  </button>
</template>
